import { CellValueChangedEvent } from 'ag-grid-enterprise';
import { APIComponentAttributes, PartialUpdateValues } from '@ardoq/api-types';
import { dateRangeOperations } from '@ardoq/date-range';
import { ExtendedRowNode, InventoryContext } from '../types';

const getFieldName = (
  { colDef }: CellValueChangedEvent<APIComponentAttributes> // column.key for component type is `type`, but the field is called 'typeId' in the component attributes
) => (colDef.field === 'type' ? 'typeId' : colDef.field);

/**
 * Detects spurious valueChanged events emitted by AG Grid during cell editor initialization.
 *
 * This issue occurs when editing fields on newly saved components due to a mismatch
 * between how the backend represents unset fields vs. how JavaScript/AG Grid handles them:
 *
 * Flow:
 * 1. User creates a new component with minimal fields:
 *    - Frontend: { _id: 'temp', model, typeId, rootWorkspace, name: '' }
 *    - Unset fields are `undefined` in JavaScript
 *
 * 2. User enters a name and saves:
 *    - Backend creates component and returns ALL fields, setting unset ones to `null`
 *    - Response: { _id: 'real-id', name: 'foo', description: null, business_rating: null, ... }
 *
 * 3. Frontend replaces the temporary row with the saved component:
 *    - Component now has explicit `null` values for unset fields
 *
 * 4. User clicks on an empty field (e.g., description):
 *    - Cell editor opens with `oldValue: null` (from the backend data)
 *
 * 5. During editor initialization:
 *    - AG Grid or the cell editor component momentarily treats the empty value as `undefined`
 *    - This triggers AG Grid's change detection: null → undefined
 *    - A valueChanged event is emitted even though the user hasn't made any changes
 *
 * Why this check is safe:
 * - Real user edits will change from `null` to an actual value (string, number, etc.), not to `undefined`
 * - This specific transition (null → undefined) only happens during editor initialization
 * - Filtering it out prevents unnecessary API calls and validation errors
 *
 * @returns true if the event is a spurious initialization event that should be ignored
 */
const isSpuriousValueChange = (
  event: CellValueChangedEvent<APIComponentAttributes, any, InventoryContext>
): boolean => {
  if (event.oldValue === null && event.newValue === undefined) {
    return true;
  }
  const valuesForPartialUpdate =
    cellValueChangedEventOperations.getValuesForPartialUpdate(event);

  return valuesForPartialUpdate.every(
    ({ oldValue, newValue }) => oldValue === newValue
  );
};

const valueOrNull = (value: any) => {
  return value === undefined ? null : value;
};

const getValuesForPartialUpdate = (
  event: CellValueChangedEvent<APIComponentAttributes, any, InventoryContext>
): PartialUpdateValues => {
  const fieldName = getFieldName(event)!;

  // Use the stored original value if it exists (from before any validation errors)
  const node = event.node as ExtendedRowNode;
  const storedOriginalValue = node._originalValues
    ? node._originalValues[fieldName]
    : undefined;
  const oldValueToUse =
    storedOriginalValue !== undefined ? storedOriginalValue : event.oldValue;

  if (
    dateRangeOperations.isDateRangeValue(oldValueToUse) ||
    dateRangeOperations.isDateRangeValue(event.newValue)
  ) {
    const startFieldName = dateRangeOperations.toStartDateName(fieldName);
    const endFieldName = dateRangeOperations.toEndDateName(fieldName);

    return [
      {
        oldValue: valueOrNull(oldValueToUse?.start),
        newValue: valueOrNull(event.newValue?.start),
        field: startFieldName,
      },
      {
        oldValue: valueOrNull(oldValueToUse?.end),
        newValue: valueOrNull(event.newValue?.end),
        field: endFieldName,
      },
    ];
  }

  return [
    {
      oldValue: valueOrNull(oldValueToUse),
      newValue: valueOrNull(event.newValue),
      field: fieldName,
    },
  ];
};

export const cellValueChangedEventOperations = {
  isSpuriousValueChange,
  getValuesForPartialUpdate,
  getFieldName,
};
